import api from './apiClient';
import {
  SystemSettings,
  StoreSetting,
  Course,
  BusinessHour,
  Hotel,
  OptionSetting,
  DashboardSettings
} from '../types/system';
import { SystemSettingsResponse } from '../types/systemSettings';
import config from '../config';
import axios from 'axios';
import authService from './authService';

// 型をエクスポート
export type { SystemSettingsResponse };

const BASE_URL = '/system';

// システム設定サービスのインターフェース
export interface ISystemSettingsService {
  getSystemSettings(): Promise<SystemSettingsResponse>;
  getBusinessHours(): Promise<BusinessHour[]>;
  getCourses(): Promise<Course[]>;
  getHotels(): Promise<Hotel[]>;
  getOptions(): Promise<OptionSetting[]>;
  updateSystemSettings(settings: Partial<SystemSettings>): Promise<SystemSettingsResponse>;
  addHotel(hotel: Omit<Hotel, 'id'>): Promise<Hotel>;
  updateHotel(hotelId: number, hotel: Hotel): Promise<Hotel>;
  deleteHotel(hotelId: number): Promise<void>;
  backfillHotelCoords(limit?: number): Promise<{ updated: number; failed: number; remaining: number; message: string }>;
  addOption(option: Omit<OptionSetting, 'id'>): Promise<OptionSetting>;
  updateOption(optionId: number, option: OptionSetting): Promise<OptionSetting>;
  deleteOption(optionId: number): Promise<void>;
  updateBusinessHour(id: number, data: Omit<BusinessHour, 'id' | 'day_name'> & { day_of_week: number }): Promise<BusinessHour>;
  createCourse(data: Omit<Course, 'id'>): Promise<Course>;
  updateCourse(id: string | number, data: Course): Promise<Course>;
  updateNotificationSettings(settings: any): Promise<void>;
  updateLineSettings(settings: any): Promise<void>;
  testLineConnection(): Promise<void>;
  updateBackupSettings(settings: any): Promise<void>;
  getBackupSettings(): Promise<any>;
  createBackup(): Promise<void>;
  restoreBackup(backupId: string): Promise<void>;
  deleteBackup(backupId: string): Promise<{ success: boolean; message?: string }>;
}

// システム設定サービスのクラス
export class SystemSettingsService implements ISystemSettingsService {
  private static instance: SystemSettingsService;
  private readonly baseUrl: string = '/system';
  private requestInProgress: boolean = false;
  private lastRequestTime: number = 0;
  private readonly minRequestInterval: number = 10000; // 最小リクエスト間隔（10秒に延長）
  private cache: { [key: string]: { data: any; timestamp: number } } = {}; // キャッシュを追加
  private readonly cacheExpiry: number = 30000; // キャッシュ有効期限（30秒）

  private constructor() {}

  public static getInstance(): SystemSettingsService {
    if (!SystemSettingsService.instance) {
      SystemSettingsService.instance = new SystemSettingsService();
    }
    return SystemSettingsService.instance;
  }

  private async wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  public async getSystemSettings(): Promise<SystemSettingsResponse> {
    const cacheKey = 'systemSettings';
    const now = Date.now();
    
    // キャッシュチェック
    if (this.cache[cacheKey] && (now - this.cache[cacheKey].timestamp) < this.cacheExpiry) {
      console.log('システム設定 - キャッシュから返します', {
        cacheAge: now - this.cache[cacheKey].timestamp,
        expiry: this.cacheExpiry
      });
      return this.cache[cacheKey].data;
    }
    
    // 前回のリクエストからの経過時間をチェック
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.minRequestInterval) {
      console.log(`システム設定 - レート制限: ${this.minRequestInterval - timeSinceLastRequest}ms 待機`);
      await this.wait(this.minRequestInterval - timeSinceLastRequest);
    }

    // 同時リクエストの防止
    if (this.requestInProgress) {
      console.log('システム設定 - リクエストが既に進行中です。キャッシュまたは待機します...');
      
      // キャッシュがあれば返す（古くても429エラーより良い）
      if (this.cache[cacheKey]) {
        console.log('システム設定 - 古いキャッシュを使用（429エラー回避）');
        return this.cache[cacheKey].data;
      }
      
      await this.wait(2000);
      return this.getSystemSettings();
    }

    // リトライ用の設定
    const maxRetries = 2;  // リトライ回数を減らす
    const baseDelay = 2000; // 待機時間を増やす
    
    let lastError: any = null;
    let token = '';

    try {
      this.requestInProgress = true;
      this.lastRequestTime = Date.now();

      // トークンを取得（authServiceを使用）
      token = authService.getToken() || '';
      if (!token) {
        console.warn('システム設定取得 - 認証トークンが見つかりません');
        this.requestInProgress = false;
        throw new Error('認証トークンがありません');
      }
      
      console.log('システム設定取得 - トークン取得成功:', { tokenLength: token.length });
    } catch (error) {
      this.requestInProgress = false;
      throw error;
    }

    try {
      console.log('システム設定取得 - apiClientを使用（分割トークン対応）');
      
      // apiClient使用（分割トークン対応、タイムアウトはapiClientで管理）
      const response = await api.get('/system/settings', { // 修正
        withCredentials: true,
        timeout: 15000, // タイムアウトを15秒に設定
        headers: {
          'Cache-Control': 'no-cache', // キャッシュ無効化
          'Pragma': 'no-cache'
        }
      });
      
      // 成功した場合はキャッシュに保存
      this.cache[cacheKey] = {
        data: response.data,
        timestamp: Date.now()
      };
      
      console.log('システム設定取得成功 - キャッシュに保存しました');
      
      this.requestInProgress = false;
      return response.data;
    } catch (error: any) {
      this.requestInProgress = false;
      console.error(`システム設定の取得に失敗:`, error);
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          console.error('認証エラー: トークンが無効です');
          throw new Error('認証エラー: ログインが必要です');
        }
        
        if (error.response?.status === 429) {
          console.error('レート制限エラー: リクエストが多すぎます');
          
          // 古いキャッシュがあれば使用
          if (this.cache[cacheKey]) {
            console.log('429エラー - 古いキャッシュを使用します');
            return this.cache[cacheKey].data;
          }
          
          // モックデータを返す
          console.log('429エラー - モックデータを返します');
          const mockData: SystemSettingsResponse = {
            success: false,
            data: {
              store_name: '',
              store_address: null,
              phone_number: '',
              line_account: '',
              language: 'ja',
              theme: 'light',
              notification_enabled: false,
              line_notification_enabled: false,
              line_notifications_enabled: false,
              email_notifications_enabled: false,
              push_notifications_enabled: false,
              sms_notifications_enabled: false,
              notification_sound_enabled: false,
              gps_tracking_interval: 60,
              map_default_zoom: 13,
              default_map_latitude: null,
              default_map_longitude: null,
              default_map_radius: 30,
              business_hours_start: '09:00',
              business_hours_end: '22:00',
              maintenance_mode: false,
              system_email: '',
              backup_enabled: false,
              backup_frequency: 'daily',
              backup_retention_days: 7,
              log_level: 'info',
              max_upload_size: 10,
              allowed_file_types: ['jpg', 'png', 'pdf'],
              notify_new_orders: false,
              notify_shifts: false,
              notify_messages: false,
              courses: [],
              hotels: [],
              options: [],
              business_hours: [],
              allow_same_day_bookings: true,
              max_booking_days_ahead: 30,
              min_booking_interval: 30,
              cancellation_deadline_hours: 24,
              cancellation_fee_rate: 100,
              service_fee_rate: 0,
              tax_rate: 10,
              distance_fee_enabled: false,
              distance_fee_per_km: 0,
              auto_backup_enabled: false,
              backup_interval_hours: 24,
              last_backup_at: null,
              line_channel_id: null,
              line_channel_secret: null,
              line_channel_token: null,
              line_webhook_url: null,
              created_at: '',
              updated_at: ''
            }
          };
          
          // モックデータもキャッシュする
          this.cache[cacheKey] = {
            data: mockData,
            timestamp: Date.now()
          };
          
          return mockData;
        }
      }
      
      throw error;
    }
  }

  async getBusinessHours(): Promise<BusinessHour[]> {
    try {
        const response = await api.get(`${this.baseUrl}/business-hours`);
        const data = response.data;
        if (data && Array.isArray(data.data)) {
          return data.data;
        }
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Business hours could not be retrieved:', error);
        throw error;
    }
  }

  async getCourses(): Promise<Course[]> {
    try {
      const response = await api.get(`${this.baseUrl}/courses`);
      const data = response.data;
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('コース設定データの取得に失敗:', error);
      throw error;
    }
  }

  async getHotels(): Promise<Hotel[]> {
    try {
      const response = await api.get(`${this.baseUrl}/hotels`);
      const data = response.data;
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('ホテルデータの取得に失敗:', error);
      throw error;
    }
  }

  async getOptions(): Promise<OptionSetting[]> {
    try {
      const response = await api.get(`${this.baseUrl}/options`);
      const data = response.data;
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('オプション設定データの取得に失敗:', error);
      throw error;
    }
  }

  public async updateSystemSettings(settings: Partial<SystemSettings>): Promise<SystemSettingsResponse> {
    try {
      // APIクライアントを使用（トークン管理は自動的に行われる）
      const response = await api.put<SystemSettingsResponse>(`${this.baseUrl}/settings`, settings);
      return response.data;
    } catch (error) {
      console.error('システム設定の更新に失敗:', error);
      throw error;
    }
  }

  async addHotel(hotelData: Omit<Hotel, 'id'>): Promise<Hotel> {
    const response = await api.post<Hotel>(`${this.baseUrl}/hotels`, hotelData);
    return response.data;
  }

  async updateHotel(hotelId: number, hotel: Hotel): Promise<Hotel> {
    try {
      const response = await api.put(`${this.baseUrl}/hotels/${hotelId}`, hotel);
      return response.data;
    } catch (error) {
      console.error('ホテルの更新に失敗:', error);
      throw error;
    }
  }

  async deleteHotel(hotelId: number): Promise<void> {
    await api.delete(`${this.baseUrl}/hotels/${hotelId}`);
  }

  // 座標が未設定のホテルを一括でジオコーディング補完する（1回あたり limit 件）
  async backfillHotelCoords(limit = 50): Promise<{ updated: number; failed: number; remaining: number; message: string }> {
    const response = await api.post(`${this.baseUrl}/hotels/backfill-coords`, { limit });
    return response.data;
  }

  async addOption(optionData: Omit<OptionSetting, 'id'>): Promise<OptionSetting> {
    const response = await api.post<OptionSetting>(`${this.baseUrl}/options`, optionData);
    return response.data;
  }

  async updateOption(optionId: number, option: OptionSetting): Promise<OptionSetting> {
    try {
      const response = await api.put(`${this.baseUrl}/options/${optionId}`, option);
      return response.data;
    } catch (error) {
      console.error('オプションの更新に失敗:', error);
      throw error;
    }
  }

  async deleteOption(optionId: number): Promise<void> {
    await api.delete(`${this.baseUrl}/options/${optionId}`);
  }

  async updateBusinessHour(id: number, data: Omit<BusinessHour, 'id' | 'day_name'> & { day_of_week: number }): Promise<BusinessHour> {
    console.log(`Sending data for day ${id}:`, data);
    try {
      const response = await api.put(`${this.baseUrl}/business-hours/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('営業時間の更新に失敗:', error);
      throw error;
    }
  }

  async createCourse(data: Omit<Course, 'id'>): Promise<Course> {
    try {
      const response = await api.post(`${this.baseUrl}/courses`, data);
      return response.data.data;
    } catch (error) {
      console.error('コースの追加に失敗:', error);
      throw error;
    }
  }

  async updateCourse(id: string | number, data: Course): Promise<Course> {
    try {
      const response = await api.put(`${this.baseUrl}/courses/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`コース設定(ID: ${id})の更新に失敗:`, error);
      throw error;
    }
  }

  async updateNotificationSettings(settings: any): Promise<void> {
    try {
      await api.put(`${this.baseUrl}/notifications`, settings);
    } catch (error) {
      console.error('通知設定の更新に失敗:', error);
      throw error;
    }
  }

  async updateLineSettings(settings: any): Promise<void> {
    try {
      await api.put(`${this.baseUrl}/line`, settings);
    } catch (error) {
      console.error('LINE設定の更新に失敗:', error);
      throw error;
    }
  }

  async testLineConnection(): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/line/test`);
    } catch (error) {
      console.error('LINE接続テストに失敗:', error);
      throw error;
    }
  }

  async updateBackupSettings(settings: any): Promise<void> {
    try {
      await api.put(`${this.baseUrl}/backup`, settings);
    } catch (error) {
      console.error('バックアップ設定の更新に失敗:', error);
      throw error;
    }
  }

  async getBackupSettings(): Promise<any> {
    try {
      const response = await api.get(`${this.baseUrl}/backup`);
      return response.data;
    } catch (error) {
      console.error('バックアップ設定の取得に失敗:', error);
      throw error;
    }
  }

  async createBackup(): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/backup`);
    } catch (error) {
      console.error('バックアップの作成に失敗:', error);
      throw error;
    }
  }

  async restoreBackup(backupId: string): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/backup/restore/${backupId}`);
    } catch (error) {
      console.error('バックアップの復元に失敗:', error);
      throw error;
    }
  }

  async deleteBackup(backupId: string): Promise<{ success: boolean; message?: string }> {
    try {
      await api.delete(`${this.baseUrl}/backup/${backupId}`);
      return { success: true };
    } catch (error: any) {
      console.error('バックアップの削除に失敗:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'バックアップの削除に失敗しました'
      };
    }
  }
}

// シングルトンインスタンスを作成してエクスポート
export const systemSettingsService = SystemSettingsService.getInstance();
export default systemSettingsService; 